<template>
  <div class="logs">
    <!-- Toolbar -->
    <div class="toolbar">
      <LgButton class="only-mobile" size="sm" variant="ghost" @click="containersOpen = true">
        <template #icon><PanelLeft :size="15" aria-hidden="true" /></template>
        Containers
      </LgButton>

      <LgInput
        v-model="searchText"
        class="search"
        placeholder="Search log messages…"
        aria-label="Search log messages"
        @keydown.enter.prevent="commitSearch"
      >
        <template #lead><Search :size="15" aria-hidden="true" /></template>
        <template #trail>
          <LgIconButton
            v-if="searchText"
            class="search-clear"
            label="Clear search"
            size="sm"
            @click="clearSearch"
          >
            <X :size="14" aria-hidden="true" />
          </LgIconButton>
        </template>
      </LgInput>

      <AttrFilterDropdown
        :containers="state.containers"
        :stacks="state.stacks"
        :range="state.range"
        :attr-count="attrCount"
        :active-attrs="activeAttrsRecord"
        @add="onAddField"
      />

      <FilterSetsMenu :query="currentQuery" @apply="onApplyFilterSet" />

      <TimeRangePicker :model-value="state.range" @update:model-value="setRange" />

      <LgTooltip :text="refreshTitle" placement="bottom">
        <LgButton
          class="live-toggle"
          :class="refreshState"
          size="sm"
          variant="ghost"
          :aria-pressed="autoRefresh"
          @click="onToggleLive"
        >
          <template #icon>
            <Pause v-if="refreshState !== 'live'" :size="14" aria-hidden="true" />
            <Play v-else :size="14" aria-hidden="true" />
          </template>
          {{ refreshLabel }}
        </LgButton>
      </LgTooltip>
    </div>

    <div class="chips-row">
      <FilterChips
        :chips="chips"
        @remove="removeChip"
        @clear="clearAll"
        @toggle-negate="toggleChipNegate"
      />
      <p v-if="attrCapNote" class="attr-cap-note" role="status">
        Attribute filter limit reached ({{ maxAttrFilters }}) — remove one first.
      </p>
      <p v-if="filterNote" class="attr-cap-note" role="status">{{ filterNote }}</p>
    </div>

    <div class="body">
      <Sidebar
        class="sidebar-desktop only-desktop"
        :containers="containers"
        :active-stacks="state.stacks"
        :active-containers="state.containers"
        :loading="containersLoading"
        :error="containersError"
        :stack-order="sidebarOrder"
        @toggle-stack="onSelectStack"
        @toggle-container="onSelectContainer"
        @refresh="() => loadContainers()"
        @reorder-stacks="onReorderStacks"
      />

      <main ref="scrollEl" class="list" @scroll="onScroll">
        <!-- Column header (dynamic: pinned-attr columns sit between container and
             message when in column mode; grid template shared with every row). -->
        <div class="list-header" :style="{ gridTemplateColumns: gridTemplate }">
          <span>time</span>
          <span>level</span>
          <span>container</span>
          <template v-if="columnMode">
            <span v-for="k in columnKeys" :key="k" class="col-pin-head" :title="k">{{ k }}</span>
          </template>
          <span>message</span>
        </div>

        <div v-if="error" class="banner error-banner" role="alert">
          <GoblinIcon class="banner-mark" variant="error" size="1.4em" />
          <span>{{ error }}</span>
          <button class="link-btn" @click="() => load(true)">Retry</button>
        </div>

        <!-- Skeleton on first load -->
        <div v-if="loading && !logs.length" class="skeleton">
          <div v-for="n in 12" :key="n" class="skeleton-row"></div>
        </div>

        <!-- Empty -->
        <div v-else-if="!logs.length && !error" class="empty">
          <GoblinIcon class="empty-mark" variant="empty" :size="80" />
          <p class="empty-title">No logs here — the goblin ate them all.</p>
          <p class="empty-sub">Try widening the time range.</p>
        </div>

        <!-- Rows -->
        <template v-else>
          <LogRow
            v-for="row in logs"
            :key="row.id"
            :row="row"
            :expanded="expandedId === row.id"
            :column-keys="columnKeys"
            :column-mode="columnMode"
            :grid-template="gridTemplate"
            :pinned-keys="pinnedKeysFor(row.container)"
            @toggle="toggleExpand(row.id)"
            @filter="onRowFilter"
            @attr-filter="onAddAttr"
            @toggle-pin="(key) => onTogglePin(row.container, key)"
            @save-default="onSaveDefault"
          />

          <div class="list-foot">
            <template v-if="pendingScanCursor !== null">
              <span class="foot-note">
                {{
                  scanStoppedAt !== null
                    ? 'Search stopped'
                    : loadingMore
                      ? 'Searching…'
                      : 'Search paused'
                }}
                <template v-if="scanScannedToTs !== null">
                  — searched back to {{ shortTime(scanScannedToTs) }}</template
                >
              </span>
              <button
                v-if="scanStoppedAt === null && loadingMore"
                class="load-more"
                @click="stopScan"
              >
                Stop
              </button>
              <button v-else class="load-more" :disabled="loadingMore" @click="resumeScan">
                Continue searching
              </button>
            </template>
            <button v-else-if="hasMore" class="load-more" :disabled="loadingMore" @click="loadMore">
              {{ loadingMore ? 'Loading…' : 'Load more' }}
            </button>
            <span v-else class="foot-note">End of results</span>
          </div>
        </template>
      </main>
    </div>

    <!-- Mobile containers drawer -->
    <LgDrawer :open="containersOpen" side="left" title="Containers" @close="containersOpen = false">
      <Sidebar
        :containers="containers"
        :active-stacks="state.stacks"
        :active-containers="state.containers"
        :loading="containersLoading"
        :error="containersError"
        :stack-order="sidebarOrder"
        embedded
        @toggle-stack="onSelectStack"
        @toggle-container="onSelectContainer"
        @refresh="() => loadContainers()"
        @reorder-stacks="onReorderStacks"
      />
    </LgDrawer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { Search, X, Play, Pause, PanelLeft } from 'lucide-vue-next';
import { api, ApiError, type LogRow as LogRowT, type ContainerInfo } from '../api.js';
import { shortTime } from '../format.js';
import {
  useFilters,
  filtersToQuery,
  filtersFromQuery,
  capPins,
  MAX_ATTR_FILTERS,
  MAX_TEXT_TERMS,
  MAX_PINNED_KEYS,
} from '../composables/useFilters.js';
import { useUserSettings } from '../composables/useUserSettings.js';
import {
  useAppSettings,
  deriveColumnKeys,
  MAX_PIN_COLUMNS,
} from '../composables/useAppSettings.js';
import Sidebar from '../components/Sidebar.vue';
import FilterChips from '../components/FilterChips.vue';
import TimeRangePicker from '../components/TimeRangePicker.vue';
import LogRow from '../components/LogRow.vue';
import AttrFilterDropdown from '../components/AttrFilterDropdown.vue';
import FilterSetsMenu from '../components/FilterSetsMenu.vue';
import LgButton from '../ui/LgButton.vue';
import LgIconButton from '../ui/LgIconButton.vue';
import LgInput from '../ui/LgInput.vue';
import LgTooltip from '../ui/LgTooltip.vue';
import LgDrawer from '../ui/LgDrawer.vue';
import GoblinIcon from '../components/GoblinIcon.vue';

const props = withDefaults(
  defineProps<{
    /**
     * Set while the settings page is open (App passes its own `settingsOpen`
     * state through). Changing a password bumps the pwv claim and invalidates
     * the current JWT the instant it happens, then SettingsView transparently
     * re-logs-in — but this view's 2s poll ran on regardless, and a tick
     * landing in that window would 401, emit `unauthorized`, and dump the user
     * back at the login screen mid re-login. Folding this into the existing
     * `paused` computed below (same mechanism as the expand/scroll auto-pause)
     * closes that race without touching the manual live/paused toggle.
     */
    settingsOpen?: boolean;
  }>(),
  { settingsOpen: false },
);

const emit = defineEmits<{ unauthorized: [] }>();

const {
  state,
  chips,
  toggleList,
  replaceSelection,
  setRange,
  removeChip,
  toggleChipNegate,
  clearAll,
  addAttrTerm,
  addTextTerm,
  toggleTextNegate,
  commitLiveQuery,
  setPins,
  replaceAll,
  apiParams,
} = useFilters();
const { sidebarOrder, loadSidebarOrder, setSidebarOrder } = useUserSettings();
const { defaultPins, loadDefaultPins, setContainerDefaultPins } = useAppSettings();

const attrCount = computed(() => state.attrs.length);
const maxAttrFilters = MAX_ATTR_FILTERS;

// AttrFilterDropdown is include-only and keyed by attr key -> value (a v0.2-shaped
// Record). Derive it from the include terms so that component stays unchanged.
const activeAttrsRecord = computed<Record<string, string>>(() => {
  const out: Record<string, string> = {};
  for (const t of state.attrs) {
    if (!t.negate && !(t.key in out)) out[t.key] = t.value;
  }
  return out;
});

// Transient "at cap" feedback (chips-row note): surfaced whenever addAttr refuses a filter
// because MAX_ATTR_FILTERS are already active, from either add path (AttrFilterDropdown or a
// row's expanded-attr click) -- both funnel through onAddAttr below. Auto-clears after a few
// seconds rather than sticking around as a permanent banner.
const ATTR_CAP_NOTE_MS = 4000;
const attrCapNote = ref(false);
let attrCapNoteTimer: ReturnType<typeof setTimeout> | undefined;

/** Shared transient note for search-term commit problems (over-long value, mirroring the attr
 * cap note above) — surfaced from `commitSearch` below via `commitLiveQuery`'s returned reason,
 * so a term that would otherwise 400 the whole `/api/query` gets a friendly explanation instead
 * of silently refusing to commit. */
const filterNote = ref('');
let filterNoteTimer: ReturnType<typeof setTimeout> | undefined;
function showFilterNote(message: string) {
  filterNote.value = message;
  clearTimeout(filterNoteTimer);
  filterNoteTimer = setTimeout(() => {
    filterNote.value = '';
  }, ATTR_CAP_NOTE_MS);
}
/**
 * What "Save current…" / "Update to current" persists. Unlike the live URL
 * (kept clean/null until the user edits pins, per the tri-state design), a
 * SAVED set must capture the pins the user actually SEES — so when `state.pins`
 * is still null (showing org defaults), materialize the capped defaults into
 * the serialized `pins` so the saved set doesn't silently lose them. Uses
 * `effectivePins` (already `state.pins ?? cappedDefaults`); an empty result
 * stays null so a save with no pins to capture doesn't bloat the set with
 * `pins={}` (and doesn't flip an inherit-defaults set to explicitly-empty).
 */
const currentQuery = computed(() => {
  const pins = state.pins ?? (Object.keys(effectivePins.value).length ? effectivePins.value : null);
  return filtersToQuery({ ...state, pins });
});

// --- Pins (session display config) ---------------------------------------
//
// v0.3 remodel: pins are now SESSION state (useFilters `state.pins`), not the
// org `containerAttrs` setting. `containerAttrs` becomes the org DEFAULT that
// seeds a fresh session (loaded into `defaultPins`).
//
// `effectivePins` is what actually drives the columns: the session pins if the
// user has resolved them (toggled a pin / loaded a set → `state.pins` non-null),
// otherwise the org defaults. This is BOTH the async-race guard AND the
// "don't serialize defaults into the URL until the user modifies" rule in one
// expression: `state.pins` stays null (→ no `pins` URL param) while defaults are
// merely displayed, and a filter-set apply or pin edit that lands before the
// defaults fetch resolves wins automatically (state.pins !== null).
// The org defaults are uncapped at their source (the App-settings editor and the
// per-container route allow far more than a session's ≤8-total pin budget), so
// they MUST be run through `capPins` before they can stand in for session pins.
// Otherwise a large default set would flow uncapped into `columnKeys`, get mapped
// per-row-per-poll (potentially hundreds of thousands of objects), and toggling a
// default-derived pin would round-trip through `setPins`→`capPins` and silently
// drop everything past the 8th. Capping here bounds every consumer at once:
// `columnKeys`/line-mode mapping, `pinnedKeysFor`, and toggle-materialization.
const cappedDefaultPins = computed<Record<string, string[]>>(
  () => capPins(defaultPins.value ?? {}) ?? {},
);
const effectivePins = computed<Record<string, string[]>>(
  () => state.pins ?? cappedDefaultPins.value,
);

/**
 * Which containers' pins should count toward the visible columns (v0.3
 * pin-behavior fix, Verra: pins are "per stack/container"). Mirrors the
 * container FILTER, but when the view is scoped to a STACK rather than
 * explicit containers (`state.stacks` set, `state.containers` empty),
 * resolve it to that stack's member containers (from the loaded containers
 * list) so columns reflect just that stack's pins instead of the global
 * union across every container. No filter at all -> empty array, which
 * `deriveColumnKeys` already treats as "all containers".
 *
 * Known edge case (acceptable, not over-built per the ask): if the stack
 * filter is applied before `/api/containers` has resolved, this briefly
 * yields an empty list — same as "no filter" — until the containers load.
 */
const columnScopeContainers = computed<string[]>(() => {
  if (state.containers.length) return state.containers;
  if (state.stacks.length) {
    const activeStacks = new Set(state.stacks);
    return containers.value
      .filter((c) => c.stack && activeStacks.has(c.stack))
      .map((c) => c.container);
  }
  return [];
});

/** Distinct pinned keys for the currently-relevant containers (current container/stack
 * filter, all if none), in insertion order. Derived from pins STATE, so columns
 * stay stable across polling/pagination. */
const columnKeys = computed(() =>
  deriveColumnKeys(effectivePins.value, columnScopeContainers.value),
);

/** Desktop shows columns only when there are some AND ≤6; otherwise the labeled
 * line takes over (even on desktop). */
const columnMode = computed(
  () => columnKeys.value.length > 0 && columnKeys.value.length <= MAX_PIN_COLUMNS,
);

/** Shared `grid-template-columns` for the list header + every row so the pinned
 * columns line up. Base = time/level/container; then one track per pinned column
 * (only in column mode); then the flexible message column. */
const gridTemplate = computed(() => {
  const base = '92px 58px minmax(90px, 180px)';
  const cols = columnMode.value
    ? ` ${columnKeys.value.map(() => 'minmax(60px, 140px)').join(' ')}`
    : '';
  return `${base}${cols} 1fr`;
});

/** Session pins for one container (used for a row's pin-button state + save-as-default). */
function pinnedKeysFor(container: string): string[] {
  return effectivePins.value[container] ?? [];
}

/** Toggle a session pin for `container`/`key`. Enforces the ≤8 total cap with a
 * friendly note instead of silently dropping (capPins would drop it). */
function onTogglePin(container: string, key: string) {
  const base = effectivePins.value;
  const current = base[container] ?? [];
  const adding = !current.includes(key);
  if (adding) {
    const total = Object.values(base).reduce((n, ks) => n + ks.length, 0);
    if (total >= MAX_PINNED_KEYS) {
      showFilterNote(`Pin limit reached (${MAX_PINNED_KEYS}) — unpin one first.`);
      return;
    }
  }
  const nextKeys = adding ? [...current, key] : current.filter((k) => k !== key);
  const next: Record<string, string[]> = { ...base };
  if (nextKeys.length) next[container] = nextKeys;
  else delete next[container];
  setPins(next); // materializes state.pins (now serialized to the URL)
}

/** "Save as default": persist this container's current session pins to the org
 * setting so they seed future fresh sessions for everyone. */
async function onSaveDefault(container: string) {
  try {
    await setContainerDefaultPins(container, pinnedKeysFor(container));
    showFilterNote(`Saved default pins for ${container}.`);
  } catch {
    showFilterNote('Failed to save default pins.');
  }
}

/**
 * Add an attr term. The row-click path (`@attr-filter`) always adds an INCLUDE
 * term (negate defaults false); the Fields dropdown can request an exclude
 * directly (negate true). Only surface the cap note for the actual "5 already
 * active" rejection -- addAttrTerm also returns false for an invalid key/value
 * or an exact duplicate, neither of which is the cap. `wouldHitCap` mirrors
 * addAttrTerm's own cap check (at the cap AND this exact term isn't already
 * present) before it mutates state.
 */
function onAddAttr(key: string, value: string, negate = false) {
  const alreadyPresent = state.attrs.some(
    (t) => t.key === key && t.value === value && t.negate === negate,
  );
  const wouldHitCap = !alreadyPresent && attrCount.value >= maxAttrFilters;
  if (addAttrTerm(key, value, negate) || !wouldHitCap) return;
  attrCapNote.value = true;
  clearTimeout(attrCapNoteTimer);
  attrCapNoteTimer = setTimeout(() => {
    attrCapNote.value = false;
  }, ATTR_CAP_NOTE_MS);
}

/**
 * Add a message (text) term with an include/exclude choice. Text-term identity
 * is by value alone (only one term per value), so "add exclude for X" reconciles
 * to: ensure a term for X exists, then set its negate to the requested mode.
 * A cap / over-long value surfaces the shared filter note rather than failing
 * silently.
 */
function addMessageTerm(value: string, negate: boolean) {
  const existing = state.text.find((t) => t.value.toLowerCase() === value.toLowerCase());
  if (existing) {
    if (existing.negate !== negate) toggleTextNegate(existing.value);
    return;
  }
  if (!addTextTerm(value)) {
    showFilterNote(`Couldn't add message filter (max ${MAX_TEXT_TERMS} text filters, ≤256 chars).`);
    return;
  }
  if (negate) toggleTextNegate(value);
}

/** Dispatch a Fields-dropdown add: message → text term, attr → attr term. */
function onAddField(
  payload:
    | { field: 'message'; value: string; negate: boolean }
    | { field: 'attr'; key: string; value: string; negate: boolean },
) {
  if (payload.field === 'message') {
    addMessageTerm(payload.value, payload.negate);
  } else {
    onAddAttr(payload.key, payload.value, payload.negate);
  }
}

function onApplyFilterSet(query: string) {
  expandedId.value = null;
  replaceAll(filtersFromQuery(query));
}

const PAGE_LIMIT = 100;
const POLL_MS = 2000;

const logs = ref<LogRowT[]>([]);
const hasMore = ref(false);
const loading = ref(false);
const loadingMore = ref(false);
const error = ref('');
const expandedId = ref<number | null>(null);

const containers = ref<ContainerInfo[]>([]);
const containersLoading = ref(false);
const containersError = ref(false);
const containersOpen = ref(false);

// --- Auto-refresh ---------------------------------------------------------
const autoRefresh = ref(true);
const scrolledAway = ref(false);
const scrollEl = ref<HTMLElement | null>(null);

// Pause polling while a row is open, the list is scrolled off the top, or the
// settings modal is open — refreshing would yank content out from under the
// reader (or race the pwv re-login above).
const paused = computed(
  () => expandedId.value !== null || scrolledAway.value || props.settingsOpen || heavySearch.value,
);

// Manual toggle always wins: turning auto-refresh off (`autoRefresh = false`)
// stays off regardless of expand/scroll state — nothing here ever flips it
// back on except the user clicking again.
type RefreshState = 'live' | 'auto-paused' | 'off';
const refreshState = computed<RefreshState>(() => {
  if (!autoRefresh.value) return 'off';
  return paused.value ? 'auto-paused' : 'live';
});

const refreshLabel = computed(() => (refreshState.value === 'live' ? 'Live' : 'Paused'));

const pauseReason = computed(() => {
  if (heavySearch.value) return 'the current search is too heavy to live-tail';
  if (props.settingsOpen) return 'settings is open';
  if (expandedId.value !== null && scrolledAway.value)
    return 'a row is expanded and the list is scrolled';
  if (expandedId.value !== null) return 'a row is expanded';
  if (scrolledAway.value) return 'the list is scrolled';
  return '';
});

const refreshTitle = computed(() => {
  switch (refreshState.value) {
    case 'live':
      return 'Auto-refresh every 2s. Pauses automatically while a row is expanded, the list is scrolled, or settings is open. Click to pause.';
    case 'auto-paused':
      return `Auto-refresh paused automatically (${pauseReason.value}). Click to turn it off manually.`;
    case 'off':
    default:
      return 'Auto-refresh is off. Click to turn it back on.';
  }
});

function onScroll() {
  const el = scrollEl.value;
  scrolledAway.value = !!el && el.scrollTop > 24;
}

/**
 * The live/paused control. Clicking while LIVE = manual pause (as before).
 * Clicking while paused (manual OFF or auto-paused) = resume tailing: re-arm
 * `autoRefresh` AND clear the auto-pause causes so `paused` actually goes false
 * (Codex finding 8 — scrolling alone won't clear `paused` while a row is
 * expanded; we must collapse it + reset the scroll-away flag), then jump to the
 * newest logs and let the 2s poll resume. `settingsOpen` is a separate race
 * guard we don't own, so it's intentionally left alone.
 */
function onToggleLive() {
  if (refreshState.value === 'live') {
    autoRefresh.value = false;
    return;
  }
  autoRefresh.value = true;
  expandedId.value = null;
  scrolledAway.value = false;
  // Explicitly resuming live overrides the heavy-search auto-pause: the user asked for it, and
  // each poll tick is now server-budget-bounded anyway.
  heavySearch.value = false;
  if (scrollEl.value) scrollEl.value.scrollTop = 0;
  load(true, true);
}

function toggleExpand(id: number) {
  expandedId.value = expandedId.value === id ? null : id;
}

function onRowFilter(kind: 'level' | 'container', value: string) {
  if (!value) return;
  toggleList(kind === 'level' ? 'levels' : 'containers', value);
}

// --- Sidebar: switch-vs-additive click, drag reorder -----------------------
function onSelectStack(stack: string, additive: boolean) {
  if (additive) toggleList('stacks', stack);
  else replaceSelection('stacks', stack);
}
function onSelectContainer(container: string, additive: boolean) {
  if (additive) toggleList('containers', container);
  else replaceSelection('containers', container);
}
function onReorderStacks(order: string[]) {
  setSidebarOrder(order).catch(() => {
    /* optimistic update already rolled back inside useUserSettings */
  });
}

// --- Search input (debounced 300ms into liveQuery) -----------------------
// The search box drives `liveQuery` (the uncommitted, live-include preview),
// debounced so we don't refetch on every keystroke. Enter commits it to a
// `text` chip (an include term). liveQuery is never persisted to the URL.
const searchText = ref(state.liveQuery);
let searchTimer: ReturnType<typeof setTimeout> | undefined;

watch(searchText, (v) => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    state.liveQuery = v;
  }, 300);
});

// Keep the input in sync when liveQuery is reset elsewhere (e.g. after commit).
watch(
  () => state.liveQuery,
  (v) => {
    if (v !== searchText.value) searchText.value = v;
  },
);

function commitSearch() {
  clearTimeout(searchTimer);
  state.liveQuery = searchText.value;
  const result = commitLiveQuery();
  if (result === 'too-long') {
    showFilterNote('Search term too long (max 256 characters) — shorten it.');
  }
}
function clearSearch() {
  searchText.value = '';
  clearTimeout(searchTimer);
  state.liveQuery = '';
}

// --- Fetching -------------------------------------------------------------
// `requestSeq` tags every load so a response that lands after a newer request
// has already been issued (e.g. a poll tick racing a filter change) is
// discarded instead of clobbering the list with stale data. `loadPending`
// tracks whether the most recently issued request is still in flight, so the
// poll loop can skip a tick rather than stack requests — manual loads (filter
// changes, retry, load more) always fire regardless of this flag.
let requestSeq = 0;
let loadPending = false;

// --- Budgeted-search scan state (server stops heavy text/attr scans early; see load()) -------
// Remaining scan position when the server budget-stopped a search before the page filled.
const pendingScanCursor = ref<number | null>(null);
// Oldest ts the scan has examined so far ("searched back to ...").
const scanScannedToTs = ref<number | null>(null);
// True once any search needed more than one budget window: pauses the 2s live tail so polling
// can't re-run an expensive scan every tick.
const heavySearch = ref(false);
// User pressed Stop during the auto-continue loop of this request seq.
let scanStopSeq = -1;
// Set when a stop actually took effect (drives the "search stopped" foot note).
const scanStoppedAt = ref<number | null>(null);

function stopScan() {
  scanStopSeq = requestSeq;
}

/** Continue a budget-stopped search from where it left off (user-initiated). */
function resumeScan() {
  scanStopSeq = -1;
  void continueScan();
}

async function continueScan() {
  const cursor = pendingScanCursor.value;
  if (cursor === null || loadPending) return;
  const seq = ++requestSeq;
  loadPending = true;
  loadingMore.value = true;
  try {
    let res = await api.query({
      ...apiParams(),
      limit: String(PAGE_LIMIT),
      scanBeforeId: String(cursor),
    });
    if (seq !== requestSeq) return;
    let added = res.logs.length;
    logs.value = [...logs.value, ...res.logs];
    scanStoppedAt.value = null;
    while (res.scanCursor !== undefined && added < PAGE_LIMIT) {
      pendingScanCursor.value = res.scanCursor;
      scanScannedToTs.value = res.scannedToTs ?? null;
      if (scanStopSeq === seq) {
        scanStoppedAt.value = res.scannedToTs ?? null;
        break;
      }
      res = await api.query({
        ...apiParams(),
        limit: String(PAGE_LIMIT - added),
        scanBeforeId: String(res.scanCursor),
      });
      if (seq !== requestSeq) return;
      added += res.logs.length;
      logs.value = [...logs.value, ...res.logs];
    }
    if (res.scanCursor === undefined) {
      pendingScanCursor.value = null;
      scanScannedToTs.value = null;
    }
    hasMore.value = res.hasMore;
    error.value = '';
  } catch (e) {
    if (e instanceof ApiError && e.unauthorized) {
      emit('unauthorized');
      return;
    }
    if (seq === requestSeq) {
      error.value = e instanceof Error ? e.message : 'Failed to load logs.';
    }
  } finally {
    if (seq === requestSeq) {
      loadingMore.value = false;
      loadPending = false;
    }
  }
}

async function load(reset: boolean, silent = false) {
  const seq = ++requestSeq;
  loadPending = true;
  if (reset) {
    if (!silent) loading.value = true;
  } else {
    loadingMore.value = true;
  }
  const params: Record<string, string> = { ...apiParams(), limit: String(PAGE_LIMIT) };
  if (!reset && logs.value.length) {
    const last = logs.value[logs.value.length - 1];
    params.beforeTs = String(last.ts);
    params.beforeId = String(last.id);
  }
  try {
    // Text/attr searches are scan-budgeted server-side: a response can stop early with a
    // scanCursor. For user-initiated loads, auto-continue (streaming results in as each bounded
    // request lands) until the page fills, the scan exhausts, or the user hits Stop. Silent
    // poll ticks never auto-continue -- a budget-heavy search re-scanned every 2s is exactly the
    // request pile-up that helped wedge prod on 2026-07-10.
    const baseLogs = reset ? [] : [...logs.value];
    let acc: LogRowT[] = [];
    let res = await api.query(params);
    if (seq !== requestSeq) return; // superseded by a newer request
    acc = res.logs;
    logs.value = [...baseLogs, ...acc];
    scanStoppedAt.value = null;
    while (res.scanCursor !== undefined && !silent && acc.length < PAGE_LIMIT) {
      pendingScanCursor.value = res.scanCursor;
      scanScannedToTs.value = res.scannedToTs ?? null;
      heavySearch.value = true; // budget-limited search: auto-pause live tail (see `paused`)
      if (scanStopSeq === seq) {
        scanStoppedAt.value = res.scannedToTs ?? null;
        break;
      }
      res = await api.query({
        ...params,
        limit: String(PAGE_LIMIT - acc.length),
        scanBeforeId: String(res.scanCursor),
      });
      if (seq !== requestSeq) return;
      acc = [...acc, ...res.logs];
      logs.value = [...baseLogs, ...acc];
    }
    if (res.scanCursor === undefined) {
      pendingScanCursor.value = null;
      scanScannedToTs.value = null;
      heavySearch.value = false; // scan completed within budget: this search can live-tail
    } else if (silent) {
      // A silent tick that came back budget-stopped: surface the partial-scan state but never
      // continue it from a poll.
      pendingScanCursor.value = res.scanCursor;
      scanScannedToTs.value = res.scannedToTs ?? null;
      heavySearch.value = true;
    }
    hasMore.value = res.hasMore;
    error.value = '';
  } catch (e) {
    if (e instanceof ApiError && e.unauthorized) {
      emit('unauthorized');
      return;
    }
    if (seq !== requestSeq) return; // superseded by a newer request
    // Don't clobber the visible list on a silent poll failure.
    if (!silent || !logs.value.length) {
      error.value = e instanceof Error ? e.message : 'Failed to load logs.';
    }
  } finally {
    if (seq === requestSeq) {
      loading.value = false;
      loadingMore.value = false;
      loadPending = false;
    }
  }
}

function loadMore() {
  load(false);
}

// `containersPending` mirrors `loadPending` above: the silent poll tick skips
// a refresh rather than stacking requests if one is still in flight, while a
// manual refresh (sidebar retry button) always fires.
let containersPending = false;

async function loadContainers(silent = false) {
  if (containersPending) return;
  containersPending = true;
  if (!silent) containersLoading.value = true;
  try {
    containers.value = await api.containers();
    containersError.value = false;
  } catch (e) {
    if (e instanceof ApiError && e.unauthorized) {
      emit('unauthorized');
      return;
    }
    // Don't clobber a previously-successful list on a silent poll failure.
    if (!silent) containersError.value = true;
  } finally {
    containersLoading.value = false;
    containersPending = false;
  }
}

// Refetch whenever any QUERY filter changes. Collapse the open row so the fresh
// list isn't anchored to a row that may no longer exist. `pins` is intentionally
// excluded: it's a display concern (columns), not a server filter, so toggling a
// pin must NOT refetch or collapse the expanded row the pin toggle lives in.
//
// NOTE: this is a hand-maintained allowlist of the query-affecting fields. Any
// NEW query field added to `Filters` (something `apiParams()` sends to
// /api/query) MUST be added to the object below too, or a change to it won't
// trigger a reload. Non-query/display fields (like `pins`) are deliberately
// omitted.
watch(
  () =>
    JSON.stringify({
      text: state.text,
      liveQuery: state.liveQuery,
      containers: state.containers,
      stacks: state.stacks,
      levels: state.levels,
      streams: state.streams,
      range: state.range,
      attrs: state.attrs,
    }),
  () => {
    expandedId.value = null;
    load(true);
  },
);

// --- Polling --------------------------------------------------------------
let pollTimer: ReturnType<typeof setInterval> | undefined;
onMounted(() => {
  load(true);
  loadContainers();
  loadSidebarOrder();
  // Seed session pins from the org defaults on a fresh session. `effectivePins`
  // reads `defaultPins` only while `state.pins` is null, so this never overrides
  // a shared-link / filter-set that already carries pins (the async-race guard),
  // and never writes the defaults into the URL.
  loadDefaultPins();
  pollTimer = setInterval(() => {
    if (autoRefresh.value && !paused.value) {
      if (!loadPending) load(true, true);
      // Sidebar dot/count freshness piggybacks on the same tick (closes the
      // v0.1 stale-dots gap) — guarded by its own in-flight flag above.
      loadContainers(true);
    }
  }, POLL_MS);
});
onBeforeUnmount(() => {
  clearInterval(pollTimer);
  clearTimeout(searchTimer);
  clearTimeout(attrCapNoteTimer);
  clearTimeout(filterNoteTimer);
});
</script>

<style scoped>
.logs {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/* Toolbar */
.toolbar {
  display: flex;
  align-items: center;
  gap: var(--lg-space-3);
  padding: var(--lg-space-2) var(--lg-space-4);
  border-bottom: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
  flex-shrink: 0;
}
.search {
  flex: 1;
  max-width: 520px;
}
/* Sized down from LgIconButton's default 28/34px so it stays compact inside
   the input's trail slot; hover/focus/touch-target behavior all come from
   the primitive. */
.search-clear {
  width: 20px;
  height: 20px;
}

/* Base sizing/chrome comes from LgButton (ghost/sm); only the tri-state
   color coding is added here:
   - live: green (auto-refreshing)
   - auto-paused: amber (temporarily paused by expand/scroll, will resume)
   - off: grey/dim (manually turned off, stays off) */
.live-toggle.live {
  color: var(--lg-ok);
  border-color: color-mix(in srgb, var(--lg-ok) 45%, var(--lg-border-strong));
}
.live-toggle.auto-paused {
  color: var(--lg-warn);
  border-color: color-mix(in srgb, var(--lg-warn) 45%, var(--lg-border-strong));
}
.live-toggle.off {
  color: var(--lg-text-faint);
}

/* Chips */
.chips-row {
  flex-shrink: 0;
  overflow-x: auto;
  overflow-y: hidden;
}
.attr-cap-note {
  margin: 0;
  padding: var(--lg-space-1) var(--lg-space-4);
  color: var(--lg-warn);
  font-size: var(--lg-fs-sm);
}

/* Body */
.body {
  display: flex;
  flex: 1;
  min-height: 0;
}
.sidebar-desktop {
  flex-shrink: 0;
}
.list {
  flex: 1;
  overflow-y: auto;
  min-width: 0;
}
.list-header {
  display: grid;
  grid-template-columns: 92px 58px minmax(90px, 180px) 1fr;
  gap: var(--lg-space-3);
  padding: var(--lg-space-2) var(--lg-space-3);
  position: sticky;
  top: 0;
  background: var(--lg-bg-raised);
  border-bottom: 1px solid var(--lg-border);
  color: var(--lg-text-faint);
  text-transform: uppercase;
  font-size: var(--lg-fs-2xs);
  letter-spacing: 0.05em;
  z-index: var(--lg-z-base);
}
/* Pinned-attr column header — ellipsize a long key rather than stretch the track. */
.col-pin-head {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.banner {
  padding: var(--lg-space-3);
  display: flex;
  align-items: center;
  gap: var(--lg-space-3);
}
.error-banner {
  background: color-mix(in srgb, var(--lg-level-error) 14%, var(--lg-bg));
  color: var(--lg-level-error);
  border-bottom: 1px solid color-mix(in srgb, var(--lg-level-error) 30%, transparent);
}
.banner-mark {
  flex-shrink: 0;
}
.link-btn {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  text-decoration: underline;
  font: inherit;
}

.skeleton {
  padding: var(--lg-space-2) var(--lg-space-3);
}
.skeleton-row {
  height: 18px;
  margin: var(--lg-space-2) 0;
  border-radius: var(--lg-radius-sm);
  background: linear-gradient(
    90deg,
    var(--lg-bg-raised) 25%,
    var(--lg-bg-hover) 50%,
    var(--lg-bg-raised) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.3s infinite;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.empty {
  text-align: center;
  padding: var(--lg-space-8) var(--lg-space-5);
  color: var(--lg-text-dim);
}
.empty-mark {
  color: var(--lg-text-dim);
  margin-bottom: var(--lg-space-4);
}
.empty-title {
  font-size: var(--lg-fs-lg);
  margin: 0 0 var(--lg-space-2);
  color: var(--lg-text);
}
.empty-sub {
  margin: 0;
  font-size: var(--lg-fs-md);
}

.list-foot {
  display: flex;
  justify-content: center;
  padding: var(--lg-space-4);
}
.load-more {
  font-family: var(--lg-font-sans);
  font-size: var(--lg-fs-sm);
  min-height: var(--lg-control-md);
  padding: 0 var(--lg-space-4);
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border-strong);
  background: var(--lg-bg-surface);
  color: var(--lg-text);
  cursor: pointer;
}
.load-more:hover:not(:disabled) {
  border-color: var(--lg-accent);
}
.load-more:disabled {
  opacity: 0.6;
  cursor: default;
}
.foot-note {
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-sm);
}

/* Responsive */
.only-mobile {
  display: none;
}
@media (max-width: 768px) {
  .only-desktop {
    display: none;
  }
  .only-mobile {
    display: inline-flex;
  }
  .toolbar {
    flex-wrap: wrap;
    gap: var(--lg-space-2);
    padding: var(--lg-space-2) var(--lg-space-3);
  }
  .search {
    order: 3;
    flex-basis: 100%;
    max-width: none;
  }
  .list-header {
    display: none;
  }
}
</style>
